import { useCallback } from 'react';

import { ChakraBox, ChakraFlex, ChakraText } from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import { Path } from '~/routes/constants';
import { UnsignedNoteItem } from '~/store/services/providerDashboard';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  getAgeFromDOB,
  getPreferredFullName,
} from '~/lib/util/patient.util';
import { getTimeElapsed } from '../provider-dashboard.util';

const DATE_FORMAT = 'yyyy-MM-dd';

interface PendingNoteRowProps {
  note: UnsignedNoteItem;
}

const PendingNoteRow = ({ note }: PendingNoteRowProps) => {
  const history = useHistory();
  const { trackEvent } = useAnalytics() ?? {};
  const {
    noteExternalId,
    title,
    createdAt,
    patientExternalId,
    patientFirstName,
    patientLastName,
    patientChosenName,
    patientPronouns,
    patientDateOfBirth,
  } = note;

  const handleClick = useCallback(() => {
    const noteAge = createdAt
      ? Math.floor(
          (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24),
        )
      : undefined;
    trackEvent?.(ProviderDashboardEvents.pendingNotesNoteClicked, {
      noteAge,
      Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
    });
    history.push(`${Path.MAUD_PATIENTS}/${patientExternalId}/notes?noteId=${noteExternalId}`);
  }, [history, patientExternalId, noteExternalId, createdAt, trackEvent]);

  const fullName =
    getPreferredFullName(
      patientChosenName ?? '',
      patientFirstName ?? '',
      patientLastName ?? '',
    ) || 'Unknown patient';
  const age = getAgeFromDOB(patientDateOfBirth ?? '', DATE_FORMAT);
  const pronounsDisplay = patientPronouns?.length
    ? patientPronouns.join(', ')
    : '';
  const { label: timeLabel, tone } = getTimeElapsed(createdAt);

  return (
    <ChakraFlex
      alignItems="center"
      borderTop="1px solid"
      borderColor="neutral.line"
      cursor="pointer"
      data-testid={`pending-note-row-${noteExternalId}`}
      gap="12px"
      onClick={handleClick}
      padding="16px 0"
      _hover={{ bg: 'neutral.background.secondary' }}
    >
      <ChakraBox flex="1" minWidth="0">
        <ChakraFlex alignItems="baseline" gap="6px" flexWrap="wrap">
          <ChakraText fontSize="14px" fontWeight="400" color="neutral.primary">
            {fullName}
          </ChakraText>
          {(pronounsDisplay || !!age) && (
            <ChakraText fontSize="13px" color="neutral.secondary">
              (
              {[pronounsDisplay, age ? `${age}yr` : '']
                .filter(Boolean)
                .join(', ')}
              )
            </ChakraText>
          )}
        </ChakraFlex>
        <ChakraText
          fontSize="13px"
          color="neutral.secondary"
          marginTop="3px"
          noOfLines={1}
        >
          {title}
        </ChakraText>
      </ChakraBox>

      <ChakraText
        className={
          tone ? `pending-note-badge pending-note-badge--${tone}` : undefined
        }
        data-testid={`pending-note-time-${noteExternalId}`}
        fontSize={tone ? undefined : '13px'}
        color={tone ? undefined : 'neutral.secondary'}
        whiteSpace={tone ? undefined : 'nowrap'}
        flexShrink={0}
      >
        {timeLabel}
      </ChakraText>
    </ChakraFlex>
  );
};

export default PendingNoteRow;
